import React, { useEffect, useState } from 'react';
import './AccessibilityPanel.css';

const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;
const DEFAULT_FONT_SIZE = 16;

const clampFontSize = (value) => Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, value));

const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [screenReaderMode, setScreenReaderMode] = useState(false);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
    document.body.classList.toggle('high-contrast', highContrast);
    document.body.classList.toggle('reduced-motion', reducedMotion);
    document.body.classList.toggle('screen-reader-mode', screenReaderMode);

    return () => {
      document.documentElement.style.fontSize = '';
      document.body.classList.remove('high-contrast', 'reduced-motion', 'screen-reader-mode');
    };
  }, [fontSize, highContrast, reducedMotion, screenReaderMode]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const togglePanel = () => {
    setIsOpen((previousState) => !previousState);
  };

  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  };

  const handleAccessibilityChange = (setting, value) => {
    switch (setting) {
      case 'fontSize': {
        const nextFontSize = clampFontSize(value);
        setFontSize(nextFontSize);
        announceToScreenReader(`Font size changed to ${nextFontSize} pixels`);
        break;
      }
      case 'highContrast':
        setHighContrast(value);
        announceToScreenReader(value ? 'High contrast mode enabled' : 'High contrast mode disabled');
        break;
      case 'reducedMotion':
        setReducedMotion(value);
        announceToScreenReader(value ? 'Reduced motion enabled' : 'Reduced motion disabled');
        break;
      case 'screenReaderMode':
        setScreenReaderMode(value);
        announceToScreenReader(value ? 'Screen reader mode enabled' : 'Screen reader mode disabled');
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div
        className={`accessibility-backdrop ${isOpen ? 'open' : ''}`}
        aria-hidden="true"
        onClick={togglePanel}
      />

      <button
        type="button"
        className={`accessibility-toggle ${isOpen ? 'open' : ''}`}
        onClick={togglePanel}
        aria-label={isOpen ? 'Close accessibility panel' : 'Open accessibility panel'}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <span className="accessibility-icon" aria-hidden="true">
          {isOpen ? '\u2715' : <span className="material-symbols-outlined">accessibility_new</span>}
        </span>
        <span className="accessibility-toggle-label-text">
          {isOpen ? 'Close' : 'Accessibility'}
        </span>
        <span className="sr-only">
          {isOpen ? 'Close accessibility options' : 'Open accessibility options'}
        </span>
      </button>

      <aside
        id="accessibility-panel"
        className={`accessibility-panel ${isOpen ? 'open' : ''}`}
        role="region"
        aria-label="Accessibility options"
        aria-hidden={!isOpen}
      >
        <div className="accessibility-header">
          <div>
            <p className="accessibility-kicker">Display Preferences</p>
            <h2>Accessibility Options</h2>
            <p className="accessibility-header-copy">
              Adjust reading comfort, visibility, and motion settings without leaving the page.
            </p>
          </div>

          <button
            type="button"
            className="close-button"
            onClick={togglePanel}
            aria-label="Close accessibility panel"
          >
            <span aria-hidden="true">{'\u2715'}</span>
          </button>
        </div>

        <div className="accessibility-content">
          <section className="accessibility-section accessibility-surface">
            <div className="accessibility-section-head">
              <div>
                <h3>Text Size</h3>
                <p className="accessibility-description">
                  Scale the interface text for improved readability.
                </p>
              </div>
              <span className="font-size-display" aria-live="polite">
                {fontSize}px
              </span>
            </div>

            <div className="font-size-controls">
              <button
                type="button"
                onClick={() => handleAccessibilityChange('fontSize', fontSize - 2)}
                aria-label="Decrease font size"
                disabled={fontSize <= MIN_FONT_SIZE}
              >
                A-
              </button>
              <button
                type="button"
                onClick={() => handleAccessibilityChange('fontSize', fontSize + 2)}
                aria-label="Increase font size"
                disabled={fontSize >= MAX_FONT_SIZE}
              >
                A+
              </button>
              <button
                type="button"
                onClick={() => handleAccessibilityChange('fontSize', DEFAULT_FONT_SIZE)}
                aria-label="Reset font size to default"
                className="reset-button"
              >
                Reset
              </button>
            </div>
          </section>

          <section className="accessibility-section accessibility-surface">
            <div className="accessibility-section-head accessibility-section-head-stack">
              <div>
                <h3>Viewing Preferences</h3>
                <p className="accessibility-description">
                  Fine-tune contrast, motion, and assistive reading behavior.
                </p>
              </div>
            </div>

            <label className="accessibility-option" htmlFor="high-contrast-toggle">
              <div className="accessibility-option-copy">
                <span className="toggle-text">High Contrast</span>
                <p id="high-contrast-description" className="accessibility-description">
                  Increases visual contrast for stronger separation between foreground and background.
                </p>
              </div>
              <span className="accessibility-switch">
                <input
                  id="high-contrast-toggle"
                  type="checkbox"
                  checked={highContrast}
                  onChange={(event) =>
                    handleAccessibilityChange('highContrast', event.target.checked)
                  }
                  aria-describedby="high-contrast-description"
                />
                <span className="accessibility-switch-track" aria-hidden="true">
                  <span className="accessibility-switch-thumb" />
                </span>
              </span>
            </label>

            <label className="accessibility-option" htmlFor="reduced-motion-toggle">
              <div className="accessibility-option-copy">
                <span className="toggle-text">Reduce Motion</span>
                <p id="reduced-motion-description" className="accessibility-description">
                  Minimizes animation and transition effects across the interface.
                </p>
              </div>
              <span className="accessibility-switch">
                <input
                  id="reduced-motion-toggle"
                  type="checkbox"
                  checked={reducedMotion}
                  onChange={(event) =>
                    handleAccessibilityChange('reducedMotion', event.target.checked)
                  }
                  aria-describedby="reduced-motion-description"
                />
                <span className="accessibility-switch-track" aria-hidden="true">
                  <span className="accessibility-switch-thumb" />
                </span>
              </span>
            </label>

            <label className="accessibility-option" htmlFor="screen-reader-toggle">
              <div className="accessibility-option-copy">
                <span className="toggle-text">Screen Reader Mode</span>
                <p id="screen-reader-description" className="accessibility-description">
                  Optimizes text structure and spacing for assistive technology workflows.
                </p>
              </div>
              <span className="accessibility-switch">
                <input
                  id="screen-reader-toggle"
                  type="checkbox"
                  checked={screenReaderMode}
                  onChange={(event) =>
                    handleAccessibilityChange('screenReaderMode', event.target.checked)
                  }
                  aria-describedby="screen-reader-description"
                />
                <span className="accessibility-switch-track" aria-hidden="true">
                  <span className="accessibility-switch-thumb" />
                </span>
              </span>
            </label>
          </section>

          <section className="accessibility-section accessibility-surface">
            <h3>Keyboard Navigation</h3>
            <div className="keyboard-help">
              <p>Use Tab and Shift+Tab to move between interactive elements.</p>
              <p>Press Enter or Space to activate controls, and Escape to close this panel.</p>
            </div>
          </section>

          <section className="accessibility-section accessibility-surface">
            <h3>Accessibility Support</h3>
            <p className="accessibility-description accessibility-support-copy">
              If you need help accessing any content, contact our team or review the full
              accessibility statement.
            </p>

            <a
              href="mailto:accessibility@rdvsystems.com"
              className="accessibility-contact"
              aria-label="Send email to accessibility support"
            >
              accessibility@rdvsystems.com
            </a>

            <div className="accessibility-links">
              <a
                href="/accessibility-statement"
                className="accessibility-link"
                aria-label="Read our full accessibility statement"
              >
                Read Accessibility Statement
              </a>
            </div>
          </section>
        </div>
      </aside>

      <div className="sr-only">
        <h1>123BIMLIVE by RDV Systems</h1>
        <p>
          Advanced infrastructure safety analysis and visualization platform for monitoring,
          analysis, detection, and reporting on critical infrastructure elements.
        </p>
      </div>
    </>
  );
};

export default AccessibilityPanel;
